// SPDX-License-Identifier: AGPL-3.0-or-later

// LOCAL-ONLY (tweet-archive-plan.md section 4, 12): an archived X/Twitter post (embed type x_post). One box: the
// replied-to post, the main post, then the quoted post inset below the main text; each part is its text and its
// media, drawn by the attachment mosaic (one item, or a grid; videos and GIFs in the attachment players). A media
// item of a post X marks sensitive is behind Fluxer's spoiler, the same as a spoiler attachment; the text stays
// visible. No author, handle, avatar, date, counts, link or label, and no blur or age gate (XPostEmbedParts).
// Obyrus draws the same parts in the same order (embed_x_post.dart). Exclude from upstream sync.

import {AttachmentMosaic} from '@app/features/channel/components/embeds/attachments/AttachmentMosaic';
import styles from '@app/features/channel/components/embeds/ChannelEmbed.module.css';
import type {EmbedProps} from '@app/features/channel/components/embeds/channel_embed/ChannelEmbedShared';
import {EmbedDescription} from '@app/features/channel/components/embeds/channel_embed/EmbedParts';
import xStyles from '@app/features/channel/components/embeds/channel_embed/XPostEmbed.module.css';
import {
	partAttachments,
	type XPostPart,
	xPostParts,
} from '@app/features/channel/components/embeds/channel_embed/XPostEmbedParts';
import markupStyles from '@app/features/theme/styles/Markup.module.css';
import {XPostRoles} from '@fluxer/constants/src/ChannelConstants';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type {FC} from 'react';
import {useMemo} from 'react';

const XPostPartView: FC<{part: XPostPart; attachmentKey: string} & Pick<EmbedProps, 'message' | 'isPreview'>> =
	observer(({part, attachmentKey, message, isPreview}) => {
		const attachments = useMemo(() => partAttachments(part, attachmentKey), [part, attachmentKey]);
		return (
			<div
				className={clsx(xStyles.part, part.role === XPostRoles.QUOTED && xStyles.quoted)}
				data-x-post-role={part.role}
				data-flx={`channel.embeds.x-post-embed.part.${part.role}`}
			>
				{part.text.length > 0 && (
					<EmbedDescription
						description={part.text}
						markdown
						messageId={message.id}
						channelId={message.channelId}
						data-flx="channel.embeds.x-post-embed.part.text"
					/>
				)}
				{attachments.length > 0 && (
					<div className={xStyles.media} data-flx="channel.embeds.x-post-embed.part.media">
						<AttachmentMosaic
							attachments={attachments}
							message={message}
							hideExpiryFootnote={true}
							isPreview={isPreview}
							data-flx="channel.embeds.x-post-embed.part.attachment-mosaic"
						/>
					</div>
				)}
			</div>
		);
	});

export const XPostEmbed: FC<EmbedProps> = observer(({embed, message, embedIndex, isPreview}) => {
	const parts = useMemo(() => xPostParts(embed), [embed]);
	if (parts.length === 0) return null;
	const key = `x-post-${message.id}-${embedIndex ?? 0}`;
	const main = parts.filter((part) => part.role !== XPostRoles.QUOTED);
	const quoted = parts.find((part) => part.role === XPostRoles.QUOTED);
	return (
		<article
			className={clsx(styles.embed, styles.embedFull, markupStyles.markup, xStyles.xPost)}
			data-search-highlight-scope="message"
			data-flx="channel.embeds.x-post-embed"
		>
			<div className={xStyles.parts}>
				{main.map((part) => (
					<XPostPartView key={part.role} part={part} attachmentKey={key} message={message} isPreview={isPreview} />
				))}
				{quoted && <XPostPartView part={quoted} attachmentKey={key} message={message} isPreview={isPreview} />}
			</div>
		</article>
	);
});
